import {MicromorgiIcon, RecurringIcon, TriangularErrorIcon} from '@/assets/icons/icons'
import Checkbox from '@/components/commons/checkbox/CheckBox'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {getLocalDate} from '@/dayjs'
import {SubscriptionsKeysE, SubscriptionsStatusE, SubscriptionV3} from '@/features/guest/types'
import {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {
    StyledPaymentError,
    StyledSupportedUser,
    StyledSupportedUserContent,
    StyledSupportedUserInform,
    StyledSupportedUserInformAmount,
    StyledSupportedUserInformName,
    StyledSupportedUserLastPaymentDate,
    radioInputStyles
} from './style'
import Radio from '@/components/commons/radio/Radio'
import {useGetCreatorProfile} from '../../queries/useGetCreatorProfile'

type SupportedUserProps = {
    subscription: SubscriptionV3
    onSelect: (action?: SubscriptionsStatusE) => void
    subscriptionType?: SubscriptionsKeysE
}

export const SupportedUser: FC<SupportedUserProps> = ({subscription, onSelect, subscriptionType}) => {
    const {spacing} = useTheme()
    const {data: host} = useGetCreatorProfile({creatorIdentifier: subscription.rookieId.toString()})
    const {t} = useTranslation()
    const error = {
        [SubscriptionsStatusE.Active]: <></>,
        [SubscriptionsStatusE.Cancel]: <></>,
        [SubscriptionsStatusE.Inactive]: <></>,
        [SubscriptionsStatusE.Failed]: (
            <StyledPaymentError>
                <TriangularErrorIcon />
                {t('leader_payments:payment_error:title')}
            </StyledPaymentError>
        )
    }[subscription.status]

    return (
        <>
            <StyledSupportedUser>
                <Checkbox
                    name={String(subscription.id)}
                    id={subscription.id.toString()}
                    defaultChecked={subscription.status === SubscriptionsStatusE.Active}
                    onChange={() => onSelect()}
                />
                <Avatar
                    imageUrl={host?.avatar?.url}
                    name={host?.fullName}
                    height={60}
                    width={60}
                    radius={spacing * 2}
                />
                <StyledSupportedUserContent>
                    <StyledSupportedUserInform>
                        <StyledSupportedUserInformName> {host?.username}</StyledSupportedUserInformName>
                        <StyledSupportedUserInformAmount>
                            <MicromorgiIcon width={21} height={21} />
                            {subscription.amount}
                        </StyledSupportedUserInformAmount>
                    </StyledSupportedUserInform>
                    {error}
                    <StyledSupportedUserLastPaymentDate>
                        <span>
                            {!subscription.failedAt ? (
                                <>
                                    {t('leader_payments:active_gifting_next_donation')}
                                    <span className="date">
                                        {getLocalDate(subscription.nextDonationAt, 'DD/MM/YYYY')}
                                    </span>
                                </>
                            ) : (
                                <>
                                    {t('leader_payments:failed_gifting_at')}
                                    <span className="date">
                                        {getLocalDate(subscription.failedAt as string, 'DD/MM/YYYY')}
                                    </span>
                                </>
                            )}
                        </span>

                        <RecurringIcon width={19} height={19} />
                    </StyledSupportedUserLastPaymentDate>
                </StyledSupportedUserContent>
            </StyledSupportedUser>

            {(subscription.status === SubscriptionsStatusE.Inactive ||
                subscription.status === SubscriptionsStatusE.Cancel) && (
                <div>
                    <Radio
                        name={`${subscription.rookieId}-subscriptions_actions`}
                        value={`${subscription.rookieId}-${SubscriptionsStatusE.Inactive}`}
                        radioInputStyles={radioInputStyles}
                        defaultChecked={true}
                        label={t('leader_payments:subscriptions_actions:keep')}
                        onClick={() => subscriptionType && onSelect(SubscriptionsStatusE.Inactive)}
                    />
                    {subscriptionType === SubscriptionsKeysE.Errors && (
                        <Radio
                            name={`${subscription.rookieId}-subscriptions_actions`}
                            value={`${subscription.rookieId}-${SubscriptionsStatusE.Cancel}`}
                            defaultChecked={false}
                            radioInputStyles={radioInputStyles}
                            label={t('leader_payments:subscriptions_actions:delete')}
                            onClick={() => subscriptionType && onSelect(SubscriptionsStatusE.Cancel)}
                        />
                    )}
                </div>
            )}
        </>
    )
}
